/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Add, Cancel, Close, Save } from "@mui/icons-material";
import {
  Card,
  CardContent,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import { Form, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import PageContainer from "@jumbo/components/PageContainer";
import LoadingButton from "@jumbo/components/JumboLoadingButton";
import Button from "@jumbo/components/JumboLoadingButton";
import moment from "moment";
import { PURCHASE_ORDERS } from "app/routes/constant";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import Loader from "@jumbo/components/Loader";
import { getVendorList } from "app/redux/slice/vendorSlice";
import { getFabricList } from "app/redux/slice/fabricSlice";
import { createPo, editPo, updatePo } from "app/redux/slice/poSlice";
import { getProductList } from "app/redux/slice/productSlice";
import { getVariantList } from "app/redux/slice/variantSlice";

const useStyles = makeStyles({
  headingTitle: {
    minWidth: "80px",
  },
  table: {
    minWidth: 650,
    "& .MuiTableCell-root": {
      borderLeft: "1px solid",
      borderBottom: "1px solid",
    },
  },
  particular: {
    minWidth: "300px",
  },
  variants: {
    minWidth: "300px",
  },
  amount: {
    minWidth: "100px",
    verticalAlign: "top",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "end",
    margin: "6px 0px",
  },
  margin: {
    "& > :first-child": {
      marginTop: "10px",
    },
    marginBottom: "10px",
  },
  alignTop: {
    verticalAlign: "top",
  },
  loadingButtonContainer: {
    display: "flex",
    justifyContent: "end",
    marginTop: "20px",
  },
  imageUploadContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    padding: "5px",
    border: "2px dashed #ccc",
    borderRadius: "8px",
    minHeight: "120px",
    justifyContent: "center",
    cursor: "pointer",
    "&:hover": {
      borderColor: "#1976d2",
    },
  },
  imagePreview: {
    width: "80px",
    height: "80px",
    objectFit: "cover",
    borderRadius: "8px",
  },
  hiddenInput: {
    display: "none",
  },
});

const createForList = [
  {
    label: "Fabric",
    _id: "Fabric",
  },
  {
    label: "Stitch",
    _id: "Stitch",
  },
  {
    label: "Modeling",
    _id: "Modeling",
  },
  {
    label: "ReadyMade",
    _id: "ReadyMade",
  },
  {
    label: "Dyer",
    _id: "Dyer",
  },
];

const AddAndUpdatePurchaseOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const supplierData = useSelector((state) => state?.vendor);
  const fabricData = useSelector((state) => state?.fabric);
  const productData = useSelector((state) => state?.product);
  const variantData = useSelector((state) => state?.variant);
  const mobileView = useMediaQuery("(max-width:575px)");
  const [loading, setLoading] = useState(false);

  const validationSchema = yup.object({
    createFor: yup.string().required("Please select a create for").nullable(),
    vendorId: yup.string().required("Please select a vendor").nullable(),
    date: yup.string().required("Please enter an order date."),
    items: yup.array().of(
      yup.object().shape({
        fabricId: yup.string(),
        productId: yup.string(),
        variantId: yup.string(),
        quantity: yup
          .number()
          .typeError("Quantity must be a number")
          .required("Please enter a quantity")
          .min(1, "Must be greater than or equal to 1"),
        rate: yup
          .number()
          .typeError("Rate must be a number")
          .required("Please enter a rate")
          .min(1, "Must be greater than or equal to 1"),
        amount: yup
          .number()
          .typeError("Amount must be a number")
          .required("Please enter an amount")
          .min(1, "Must be greater than or equal to 1"),
      })
    ),
  });

  const removeBlankStringKeys = (obj) =>
    Array.isArray(obj)
      ? obj.map(removeBlankStringKeys)
      : obj && typeof obj === "object"
      ? Object.fromEntries(
          Object.entries(obj)
            .filter(([_, v]) => v !== "")
            .map(([k, v]) => [k, removeBlankStringKeys(v)])
        )
      : obj;

  const formik = useFormik({
    initialValues: {
      createFor: "",
      vendorId: null,
      date: moment(new Date()).format("YYYY-MM-DD"),
      totalAmount: "",
      remark: "",
      items: [
        {
          fabricId: "",
          variantId: "",
          productId: "",
          quantity: "",
          rate: "",
          amount: "",
          fold: "",
          foldMeter: "",
        },
      ],
    },
    validationSchema: validationSchema,
    validateOnChange: false,
    validate: (values) => {
      const errors = {};

      // Custom validation for items based on createFor
      if (values.createFor === "Fabric" || values.createFor === "Dyer") {
        values.items.forEach((item, index) => {
          if (!item.fabricId) {
            if (!errors.items) errors.items = [];
            if (!errors.items[index]) errors.items[index] = {};
            errors.items[index].fabricId = "Please select a fabric";
          }
        });
      }

      if (
        values.createFor === "Modeling" ||
        values.createFor === "Stitch" ||
        values.createFor === "ReadyMade"
      ) {
        values.items.forEach((item, index) => {
          if (!item.productId) {
            if (!errors.items) errors.items = [];
            if (!errors.items[index]) errors.items[index] = {};
            errors.items[index].productId = "Please select a product";
          }

          // Check if the product has variants
          const productVariants =
            variantData?.list?.filter(
              (data) => data?.productId === item?.productId
            ) ?? [];

          // Only require variantId if the product has variants
          if (productVariants.length > 0 && !item.variantId) {
            if (!errors.items) errors.items = [];
            if (!errors.items[index]) errors.items[index] = {};
            errors.items[index].variantId = "Please select a variant";
          }
        });
      }

      return errors;
    },
    onSubmit: async (values, { resetForm, setFieldError, setSubmitting }) => {
      const newItems = removeBlankStringKeys(values?.items);
      const newValues = { ...values, items: newItems };

      try {
        const response = await dispatch(
          id ? updatePo({ id, data: newValues }) : createPo(newValues)
        ).unwrap();

        if (response.status === 200) {
          enqueueSnackbar(response.data?.responseMessage, {
            variant: "success",
          });
          navigate(PURCHASE_ORDERS);
          resetForm();
        } else {
          enqueueSnackbar(response.data?.responseMessage, {
            variant: "error",
          });
        }
      } catch (error) {
        enqueueSnackbar(error?.data?.responseMessage, {
          variant: "error",
        });
      }
      setSubmitting(false);
    },
  });

  const { values, setFieldValue, errors } = formik;
  const { isSubmitting } = formik;

  const calculateTotalAmount = (items) => {
    return items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  };

  const handleChange = (index, field, value) => {
    let updatedFields = [...values.items];
    updatedFields[index][field] = value;

    // Clear variantId when productId changes
    if (field === "productId") {
      updatedFields[index].variantId = "";
    }

    // Calculate amount based on createFor type
    if (field === "quantity" || field === "rate" || field === "foldMeter") {
      const rate = Number(updatedFields[index].rate) || 0;
      let quantity = 0;

      if (values.createFor === "Fabric") {
        const foldMeter = Number(updatedFields[index].foldMeter) || 0;
        if (foldMeter > 0) {
          quantity = foldMeter;
        } else {
          quantity = Number(updatedFields[index].quantity) || 0;
        }
      } else {
        quantity = Number(updatedFields[index].quantity) || 0;
      }
      updatedFields[index].amount = quantity * rate;
    }

    const totalAmount = calculateTotalAmount(updatedFields);

    setFieldValue("items", updatedFields);
    setFieldValue("totalAmount", totalAmount);

    // Explicitly clear variantId field when productId changes
    if (field === "productId") {
      setFieldValue(`items[${index}].variantId`, "");
    }
  };

  const handleAddMore = () => {
    const newItems = [
      ...formik.values.items,
      {
        fabricId: "",
        variantId: "",
        productId: "",
        quantity: "",
        rate: "",
        amount: "",
      },
    ];
    formik.setValues({
      ...formik.values,
      items: newItems,
      totalAmount: calculateTotalAmount(newItems),
    });
  };

  const handleDeleteField = (index) => {
    let updatedFields = [...values?.items];
    updatedFields.splice(index, 1);
    setFieldValue("items", updatedFields);
    setFieldValue("totalAmount", calculateTotalAmount(updatedFields));
  };

  useEffect(() => {
    if (id) {
      setLoading(true);
      dispatch(editPo(id))
        .unwrap()
        .then((response) => {
          if (response?.status === 200) {
            const purchaseOrderDetail = response?.data?.data;

            setFieldValue("createFor", purchaseOrderDetail?.createFor);
            setFieldValue("vendorId", purchaseOrderDetail?.vendorId?._id);
            setFieldValue(
              "date",
              moment(purchaseOrderDetail?.date).format("YYYY-MM-DD")
            );
            setFieldValue("totalAmount", purchaseOrderDetail?.totalAmount);
            setFieldValue("remark", purchaseOrderDetail?.remark);

            let productArr = [];
            for (let i = 0; i < purchaseOrderDetail?.items.length; i++) {
              const element = purchaseOrderDetail?.items[i];
              productArr?.push({
                fabricId: element?.fabricId?._id,
                variantId: element?.variantId?._id,
                productId: element?.productId?._id,
                quantity: element?.quantity,
                rate: element?.rate,
                amount: element?.amount,
                fold: element?.fold,
                foldMeter: element?.foldMeter,
              });
            }
            setFieldValue("items", productArr);

            setLoading(false);
          } else {
            enqueueSnackbar(response.data?.responseMessage, {
              variant: "error",
            });
            setLoading(false);
          }
        });
    }
  }, [dispatch, enqueueSnackbar, id, setFieldValue]);

  useEffect(() => {
    dispatch(getVendorList({ sort: 1, field: "name" }));
    dispatch(getFabricList({ sort: 1, field: "name" }));
    dispatch(getProductList({ sort: 1, field: "name" }));
    dispatch(getVariantList());
  }, []);

  const { handleSubmit } = formik;

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <PageContainer
          heading={id ? "Update Purchase Order" : "Add Purchase Order"}
        >
          <Card>
            <CardContent>
              <FormikProvider value={formik}>
                <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
                  <Grid container border={1}>
                    <Grid sm={4} xs={12} borderRight={{ xs: 0, sm: 1 }} p={1}>
                      <JumboAutoComplete
                        options={createForList ?? []}
                        label="Create For*"
                        size="small"
                        disabled={id ? true : false}
                        getOptionLabel={(option) => option?.label}
                        name={"createFor"}
                        value={createForList?.find(
                          (item) => item?._id === values?.createFor
                        )}
                        onChange={(evt, value) => {
                          setFieldValue("createFor", value?._id);
                          setFieldValue("vendorId", "");
                          setFieldValue("items", [
                            {
                              fabricId: "",
                              variantId: "",
                              productId: "",
                              quantity: "",
                              rate: "",
                              amount: "",
                            },
                          ]);
                        }}
                        helperText={formik?.errors?.createFor}
                        isOptionEqualToValue={(option, value) =>
                          option?._id === value?._id
                        }
                      />
                    </Grid>
                    <Grid sm={5} xs={12} borderRight={{ xs: 0, sm: 1 }} p={1}>
                      <JumboAutoComplete
                        options={
                          supplierData?.list?.filter((data) =>
                            data?.createFor?.includes(values?.createFor)
                          ) ?? []
                        }
                        label="Vendor*"
                        size="small"
                        getOptionLabel={(option) => option?.name}
                        name={"vendorId"}
                        value={
                          supplierData?.list?.find(
                            (item) => item?._id === values?.vendorId
                          ) || null
                        }
                        onChange={(evt, value) => {
                          setFieldValue("vendorId", value?._id);
                        }}
                        helperText={formik?.errors?.vendorId}
                        isOptionEqualToValue={(option, value) =>
                          option?._id === value?._id
                        }
                      />
                    </Grid>
                    <Grid item sm={3} xs={12} p={1}>
                      <TextField
                        label={"Date*"}
                        name="date"
                        fullWidth
                        size="small"
                        type="date"
                        onChange={(evt) =>
                          setFieldValue("date", evt.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        value={values?.date}
                        error={errors?.date}
                        helperText={errors?.date}
                      />
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    borderRight={1}
                    sx={{
                      mt: { xs: 3, md: 0 },
                    }}
                    className="mobile-view-table"
                  >
                    <TableContainer component={Paper}>
                      <Table
                        sx={{ minWidth: 650 }}
                        size="small"
                        aria-label="a dense table"
                        className={`${classes?.table}`}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell>Sr. No.</TableCell>
                            {values?.createFor === "Fabric" ||
                            values?.createFor === "Dyer" ? (
                              <TableCell style={{ minWidth: "200px" }}>
                                Fabric
                              </TableCell>
                            ) : (
                              ""
                            )}
                            {values?.createFor === "Modeling" ||
                            values?.createFor === "ReadyMade" ||
                            values?.createFor === "Stitch" ? (
                              <TableCell style={{ minWidth: "200px" }}>
                                Product
                              </TableCell>
                            ) : (
                              ""
                            )}
                            {values?.createFor === "Modeling" ||
                            values?.createFor === "ReadyMade" ||
                            values?.createFor === "Stitch" ? (
                              <TableCell style={{ minWidth: "200px" }}>
                                Variant
                              </TableCell>
                            ) : (
                              ""
                            )}
                            <TableCell style={{ minWidth: "120px" }}>
                              Quantity{" "}
                              {values?.createFor === "Fabric"
                                ? "(in meter)"
                                : ""}
                            </TableCell>
                            {values?.createFor === "Fabric" && (
                              <>
                                <TableCell style={{ minWidth: "120px" }}>
                                  Fold
                                </TableCell>
                                <TableCell style={{ minWidth: "120px" }}>
                                  Fold Meter
                                </TableCell>
                              </>
                            )}
                            <TableCell style={{ minWidth: "170px" }}>
                              Rate
                            </TableCell>
                            <TableCell
                              style={{ minWidth: "160px", textAlign: "end" }}
                            >
                              Amount
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {values?.items?.length &&
                            fabricData?.list?.length &&
                            values?.items?.map((item, index) => {
                              return (
                                <TableRow key={index}>
                                  <TableCell
                                    className={classes?.alignTop}
                                    sx={{ width: "10px", paddingTop: "20px" }}
                                  >
                                    {index + 1}
                                  </TableCell>
                                  {values?.createFor === "Fabric" ||
                                  values?.createFor === "Dyer" ? (
                                    <TableCell
                                      className={`${classes?.alignTop}`}
                                    >
                                      <JumboAutoComplete
                                        sx={{ marginTop: "7px" }}
                                        options={fabricData?.list ?? []}
                                        label="Fabric*"
                                        size="small"
                                        getOptionLabel={(option) =>
                                          option?.name
                                        }
                                        name={`items[${index}].fabricId`}
                                        value={fabricData?.list?.find(
                                          (data) => data?._id === item?.fabricId
                                        )}
                                        onChange={(evt, value) => {
                                          handleChange(
                                            index,
                                            "fabricId",
                                            value?._id
                                          );
                                        }}
                                        helperText={
                                          formik?.errors?.items?.length &&
                                          formik?.errors?.items[index]?.fabricId
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                          option?._id === value?._id
                                        }
                                      />
                                    </TableCell>
                                  ) : (
                                    ""
                                  )}
                                  {values?.createFor === "Modeling" ||
                                  values?.createFor === "ReadyMade" ||
                                  values?.createFor === "Stitch" ? (
                                    <TableCell
                                      className={`${classes?.alignTop}`}
                                    >
                                      <JumboAutoComplete
                                        sx={{ marginTop: "7px" }}
                                        options={productData?.list ?? []}
                                        label="Product*"
                                        size="small"
                                        getOptionLabel={(option) =>
                                          option?.name
                                        }
                                        disabled={
                                          id
                                            ? values?.createFor === "ReadyMade"
                                              ? false
                                              : true
                                            : false
                                        }
                                        name={`items[${index}].productId`}
                                        value={productData?.list?.find(
                                          (data) =>
                                            data?._id === item?.productId
                                        )}
                                        onChange={(evt, value) => {
                                          handleChange(
                                            index,
                                            "productId",
                                            value?._id
                                          );
                                        }}
                                        helperText={
                                          formik?.errors?.items?.length &&
                                          formik?.errors?.items[index]
                                            ?.productId
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                          option?._id === value?._id
                                        }
                                      />
                                    </TableCell>
                                  ) : (
                                    ""
                                  )}
                                  {values?.createFor === "Modeling" ||
                                  values?.createFor === "ReadyMade" ||
                                  values?.createFor === "Stitch" ? (
                                    <TableCell
                                      className={`${classes?.alignTop}`}
                                    >
                                      <JumboAutoComplete
                                        sx={{ marginTop: "7px" }}
                                        options={
                                          variantData?.list?.filter(
                                            (data) =>
                                              data?.productId ===
                                              item?.productId
                                          ) ?? []
                                        }
                                        label={
                                          variantData?.list?.filter(
                                            (data) =>
                                              data?.productId ===
                                              item?.productId
                                          )?.length > 0
                                            ? "Variant*"
                                            : "Variant"
                                        }
                                        disabled={
                                          id
                                            ? values?.createFor === "ReadyMade"
                                              ? false
                                              : true
                                            : false
                                        }
                                        size="small"
                                        getOptionLabel={(option) =>
                                          option?.name
                                        }
                                        name={`items[${index}].variantId`}
                                        value={
                                          item?.variantId
                                            ? variantData?.list?.find(
                                                (data) =>
                                                  data?._id === item?.variantId
                                              )
                                            : null
                                        }
                                        // Debug: log the variant value
                                        onFocus={() => {
                                          console.log(
                                            "Variant field focused, item.variantId:",
                                            item?.variantId
                                          );
                                        }}
                                        onChange={(evt, value) => {
                                          handleChange(
                                            index,
                                            "variantId",
                                            value?._id
                                          );
                                        }}
                                        helperText={
                                          formik?.errors?.items?.length &&
                                          formik?.errors?.items[index]
                                            ?.variantId
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                          option?._id === value?._id
                                        }
                                      />
                                    </TableCell>
                                  ) : (
                                    ""
                                  )}
                                  <TableCell className={`${classes?.alignTop}`}>
                                    <TextField
                                      sx={{ marginTop: "7px" }}
                                      placeholder="Qty"
                                      label="Qty*"
                                      name={`items[${index}].quantity`}
                                      fullWidth
                                      value={item?.quantity}
                                      size="small"
                                      type="number"
                                      onChange={(evt) =>
                                        handleChange(
                                          index,
                                          "quantity",
                                          +evt.target.value
                                        )
                                      }
                                      error={
                                        errors?.items?.length &&
                                        errors?.items?.[index]?.quantity
                                      }
                                      helperText={
                                        errors?.items?.length &&
                                        errors?.items?.[index]?.quantity
                                      }
                                    />
                                  </TableCell>
                                  {values?.createFor === "Fabric" && (
                                    <>
                                      <TableCell
                                        className={`${classes?.alignTop}`}
                                      >
                                        <TextField
                                          sx={{ marginTop: "7px" }}
                                          placeholder="Fold"
                                          label="Fold"
                                          name={`items[${index}].fold`}
                                          fullWidth
                                          value={item?.fold}
                                          size="small"
                                          type="number"
                                          onChange={(evt) =>
                                            handleChange(
                                              index,
                                              "fold",
                                              +evt.target.value
                                            )
                                          }
                                          error={
                                            errors?.items?.length &&
                                            errors?.items?.[index]?.fold
                                          }
                                          helperText={
                                            errors?.items?.length &&
                                            errors?.items?.[index]?.fold
                                          }
                                        />
                                      </TableCell>
                                      <TableCell
                                        className={`${classes?.alignTop}`}
                                      >
                                        <TextField
                                          sx={{ marginTop: "7px" }}
                                          placeholder="Fold Meter"
                                          label="Fold Meter*"
                                          name={`items[${index}].foldMeter`}
                                          fullWidth
                                          value={item?.foldMeter}
                                          size="small"
                                          type="number"
                                          onChange={(evt) =>
                                            handleChange(
                                              index,
                                              "foldMeter",
                                              +evt.target.value
                                            )
                                          }
                                          error={
                                            errors?.items?.length &&
                                            errors?.items?.[index]?.foldMeter
                                          }
                                          helperText={
                                            errors?.items?.length &&
                                            errors?.items?.[index]?.foldMeter
                                          }
                                        />
                                      </TableCell>
                                    </>
                                  )}
                                  <TableCell className={`${classes?.alignTop}`}>
                                    <TextField
                                      sx={{ marginTop: "7px" }}
                                      placeholder="Rate"
                                      label="Rate*"
                                      name={`items[${index}].rate`}
                                      fullWidth
                                      value={item?.rate}
                                      size="small"
                                      type="number"
                                      onChange={(evt) =>
                                        handleChange(
                                          index,
                                          "rate",
                                          +evt.target.value
                                        )
                                      }
                                      error={
                                        errors?.items?.length &&
                                        errors?.items?.[index]?.rate
                                      }
                                      helperText={
                                        errors?.items?.length &&
                                        errors?.items?.[index]?.rate
                                      }
                                    />
                                  </TableCell>
                                  <TableCell
                                    sx={{ width: "10px", textAlign: "end" }}
                                  >
                                    {item?.amount || 0}
                                  </TableCell>

                                  <TableCell sx={{ width: "10px" }}>
                                    {values?.items?.length > 1 && (
                                      <IconButton
                                        aria-label="delete"
                                        color="secondary"
                                        onClick={() => handleDeleteField(index)}
                                      >
                                        <Cancel />
                                      </IconButton>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}

                          <TableRow>
                            <TableCell
                              colSpan={
                                values?.createFor === "Fabric"
                                  ? 6
                                  : values?.createFor === "Dyer"
                                  ? 4
                                  : values?.createFor === "Modeling" ||
                                    values?.createFor === "Stitch" ||
                                    values?.createFor === "ReadyMade"
                                  ? 5
                                  : 3
                              }
                            >
                              <b>Total Amount:</b>
                            </TableCell>
                            <TableCell style={{ textAlign: "end" }}>
                              <b>{values?.totalAmount || 0}</b>
                            </TableCell>
                            <TableCell>
                              <Grid className={classes?.buttonContainer}>
                                <Button
                                  style={{ minWidth: "100px" }}
                                  type="button"
                                  buttonLabel={"Add"}
                                  startIcon={<Add />}
                                  variant="outlined"
                                  onClick={handleAddMore}
                                  className="add-btn"
                                />
                              </Grid>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              colSpan={
                                values?.createFor === "Fabric"
                                  ? 8
                                  : values?.createFor === "Dyer"
                                  ? 6
                                  : values?.createFor === "Modeling" ||
                                    values?.createFor === "Stitch" ||
                                    values?.createFor === "ReadyMade"
                                  ? 7
                                  : 5
                              }
                            >
                              <TextField
                                sx={{ marginTop: "7px" }}
                                placeholder="Remark"
                                label="Remark"
                                name={`remark`}
                                fullWidth
                                value={values?.remark}
                                size="small"
                                onChange={(evt) =>
                                  setFieldValue("remark", evt.target.value)
                                }
                              />
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                  <Grid container spacing={3}>
                    <Grid
                      item
                      sm={12}
                      xs={12}
                      className={classes?.loadingButtonContainer}
                    >
                      <LoadingButton
                        type="submit"
                        buttonLabel={id ? "Update" : "Save"}
                        sx={{ marginRight: "10px" }}
                        startIcon={<Save />}
                        variant="outlined"
                        fullWidth={mobileView}
                        loading={isSubmitting}
                      />
                      <LoadingButton
                        color={"secondary"}
                        buttonLabel={"Close"}
                        variant="outlined"
                        startIcon={<Close />}
                        onClick={() => navigate(PURCHASE_ORDERS)}
                        fullWidth={mobileView}
                      />
                    </Grid>
                  </Grid>
                </Form>
              </FormikProvider>
            </CardContent>
          </Card>
        </PageContainer>
      )}
    </>
  );
};

export default AddAndUpdatePurchaseOrder;
